<template>
  <div>
    <div
      v-if="loading"
      class="fixed top-54 left-108 z-10 grid place-items-center"
      :style="{
        background: 'rgba(255,255,255, 0.5)',
        width: 'calc(100vw - 22rem - 5rem - 0.5rem)',
        height: 'calc(100vh - 13.5rem - 0.5rem)'
      }"
    >
      <div class="blum-loader"></div>
    </div>

    <div class="h-12 flex sticky z-20 top-42 bg-slate-200">
      <div
        class="h-12 w-20 sticky z-10 left-88 flex-shrink-0 bg-slate-200 border-b border-slate-400"
      ></div>
      <div
        v-for="(n, idx) in headers"
        :key="idx"
        class="h-12 py-1 px-2 flex-shrink-0 flex items-center justify-center border-b border-slate-400"
        :style="{ width: `calc(${slotWidth}` }"
      >
        <div v-if="n.highlight" class="h-7 flex items-center gap-1">
          <BlTextBody2 :text="n.label.split(' ')[0]" />
          <div
            class="h-7 w-7 flex items-center justify-center rounded-full bg-light-purple-400"
          >
            <BlTextBody2
              :text="n.label.split(' ').pop()"
              class="text-pure-white"
            />
          </div>
        </div>
        <div
          v-else-if="enableBranch && n.branch"
          class="h-7 px-2 flex items-center justify-center rounded truncate"
          :style="{ backgroundColor: n.branch.background_color || 'unset' }"
          :title="`${n.label} - ${n.branch.name}`"
        >
          <BlTextBody2
            class="whitespace-no-wrap truncate"
            :text="n.label"
            :style="{ color: n.branch.color || 'unset' }"
          />
        </div>
        <div v-else class="h-7 truncate px-4" :title="n.label">
          <BlTextBody2 :text="n.label" class="truncate" />
        </div>
      </div>
    </div>

    <div class="flex flex-col relative bg-slate-200">
      <div
        v-for="time in clockRound"
        :key="time"
        class="flex"
        :id="`time_view_${time}`"
      >
        <div
          class="w-20 py-2 px-3 flex-shrink-0 sticky z-10 left-88 bg-slate-200 flex justify-end border-b border-r border-slate-400 transition-all duration-300"
          :style="{ height: `${slotHeight * 2}rem` }"
        >
          <BlTextBody2 :text="time" />
        </div>
        <div
          v-for="(n, idx) in headers"
          :key="idx"
          class="flex-shrink-0 flex flex-col gap-2 items-center justify-center border-b border-r border-slate-400 bg-slate-200 transition-all duration-300"
          :style="{
            height: `${slotHeight * 2}rem`,
            width: `calc(${slotWidth}`
          }"
        >
          <div
            v-for="item in [time, `${time.split(':')[0]}:30`]"
            :key="item"
            class="rounded-2xl border-2 border-dashed border-light-purple cursor-pointer flex flex-col items-center justify-center gap-1 opacity-0 hover:opacity-100 transition-all duration-300 bg-diagonal-stripes"
            :style="{
              height: `${slotHeight}rem`,
              width: 'calc(100% - 0.5rem)'
            }"
            @click="onOpenSlot({ dimension, time: item, header: n })"
          >
            <div v-if="!isCompact" class="text-2xl text-light-purple">+</div>
            <BlTextBody2 text="Reserve room" class="text-light-purple" />
          </div>
        </div>
      </div>
      <div
        v-if="!loading"
        class="absolute flex items-center"
        :style="{
          opacity: currentTime.opacity,
          top: currentTime.top,
          left: `calc(${currentTime.left}`,
          zIndex: 1
        }"
      >
        <div class="rounded h-2 w-2 flex-shrink-0 bg-red-cherry"></div>
        <div
          class="h-1 bg-red-cherry"
          :style="{ width: `calc(${currentTime.width}` }"
        ></div>
      </div>
      <AvailabilitiesSlot
        v-for="(item, idx) in slots"
        :key="idx"
        :isCompact="isCompact"
        :item="item"
        :headers="headers"
        :slotHeight="slotHeight"
        :slotWidth="slotWidth"
        @after-commit="$emit('after-commit')"
      />
      <div class="fixed bottom-0 right-0 z-40 p-6">
        <div
          class="h-25 w-25 flex flex-col items-center justify-center gap-1 bg-light-purple hover:bg-dark-purple-200 rounded-full border-2 border-pure-white shadow-lg cursor-pointer"
          title="New Availability"
          @click="onOpenSlot({ dimension: '' })"
        >
          <div class="text-2xl text-pure-white">+</div>
          <BlTextSubtitle3 class="text-pure-white" text="New" />
        </div>
      </div>
    </div>

    <NewAvailabilitiesModal
      v-if="newAvailabilitiesOpened"
      :selectedTherapist="newAvailabilities.therapist"
      :selectedRoom="newAvailabilities.room"
      :selectedStartDate="newAvailabilities.startDate"
      :selectedStartTime="newAvailabilities.startTime"
      @close-modal="newAvailabilitiesOpened = false"
      @after-commit="$emit('after-commit')"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { CLOCK_ROUND } from "@/plugins/assets.js";
import moment from "moment";

import NewAvailabilitiesModal from "./NewAvailabilitiesModal.vue";
import AvailabilitiesSlot from "./Slot.vue";

import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlIcon from "../../BlIcon.vue";

export default {
  name: "AvailabilitiesTimeslots",
  components: {
    NewAvailabilitiesModal,
    AvailabilitiesSlot,
    BlTextSubtitle3,
    BlTextBody2,
    BlIcon
  },
  props: {
    isCompact: { type: Boolean, default: false },
    selectedTherapist: { required: false, default: null },
    selectedRoom: { required: false, default: null },
    selectedDate: { required: false, default: null },
    headers: { type: Array, required: true },
    loading: { type: Boolean, default: false }
  },
  data() {
    return {
      newAvailabilitiesOpened: false,
      newAvailabilities: {
        therapist: null,
        room: null,
        startDate: "",
        startTime: ""
      },
      clockRound: CLOCK_ROUND
    };
  },
  computed: {
    ...mapGetters(["currentUser", "currentCenter", "availabilities"]),
    enableBranch() {
      return this.currentCenter.enable_branch;
    },
    dimension() {
      const searchParams = this.$route.query;
      return searchParams["type"];
    },
    slotHeight() {
      // retun value in rem, 1 slot is 30 minutes
      if (this.isCompact) return 6.25 / 2;
      return 15 / 2;
    },
    slotWidth() {
      if (this.headers.length > 7) return "12rem";
      else return `(100vw - 22rem - 5rem - 0.5rem) / ${this.headers.length}`;
    },
    currentTime() {
      let opacity = "100%";
      const full = `${this.slotWidth} * ${this.headers.length}`;
      const oneSlot = this.slotWidth;
      let width = "0px";

      const hours = moment().format("H");
      const minutes = moment().format("mm");
      const timeInMinutes = Number(hours) * 60 + Number(minutes);
      const top = (timeInMinutes / 60) * (this.slotHeight * 2) + "rem";

      let left = `5rem`;
      if (this.dimension === "daily") {
        width = full + " - 0.5rem";
        const isSameDay =
          moment().format("YYYY-MM-DD") ===
          moment(this.selectedDate).format("YYYY-MM-DD");
        if (!isSameDay) opacity = "0%";
      }

      if (this.dimension === "weekly") {
        width = oneSlot + " - 0.5rem";
        const indexOfToday = this.headers.findIndex(i => i.highlight);
        if (indexOfToday > -1) {
          const loop = indexOfToday;
          for (let index = 0; index < loop; index++) {
            left += ` + ${oneSlot}`;
          }
        } else opacity = "0%";
      }

      return { opacity, width, top, left };
    },
    slots() {
      const arr = [];
      const keyHeaders = this.headers.map(i => i.value);

      this.availabilities.forEach(body => {
        const date = body.date ? body.date : null;
        const room = body.room ? body.room : null;
        const key = date ? { id: date, name: date } : room;
        body.availabilities.forEach(item => {
          const usedDate =
            date || moment(this.selectedDate).format("YYYY-MM-DD");
          arr.push({ ...item, key, date: usedDate });
        });
      });
      return arr.filter(i => keyHeaders.includes(i.key.id));
    }
  },
  mounted() {
    setTimeout(() => {
      this.onScrollToTime("07:00");
    }, 1000);
  },
  methods: {
    ...mapActions(["showToast"]),
    onScrollToTime(time) {
      const id = `time_view_${time}`;
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    },
    getSlotCount(start, end) {
      const s = start.split(":").map(i => Number(i));
      const e = end.split(":").map(i => Number(i));
      let slots = 0;
      slots += (e[0] - s[0]) * 2;
      if (s[1] === 30) slots -= 1;
      if (e[1] === 30) slots += 1;
      return slots;
    },
    onOpenSlot({ dimension, time, header }) {
      if (
        this.selectedTherapist &&
        this.selectedTherapist.status === "deactivated"
      ) {
        const title = "Unable to create availability";
        const message =
          "Failed! Deactivated therapist unable to create availability.";
        this.showToast({ title, message, type: "error" });
        return;
      }
      if (dimension === "daily") {
        this.newAvailabilities = {
          therapist: this.selectedTherapist,
          room: header,
          startDate: moment(this.selectedDate).format("YYYY-MM-DD"),
          startTime: time
        };
      } else if (dimension === "weekly") {
        this.newAvailabilities = {
          therapist: this.selectedTherapist,
          room: this.selectedRoom,
          startDate: header.value,
          startTime: time
        };
      } else {
        this.newAvailabilities = {
          therapist: null,
          room: null,
          startDate: "",
          startTime: ""
        };
      }
      this.newAvailabilitiesOpened = true;
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/availabilities/timeslot/Timeslots.vue